import React from "react";
import { styled } from "@mui/material/styles";

import Interaction from "../../../models/interactions/Interaction";
import InteractionNode from "../../../models/interactions/InteractionNode";
import DragIndicatorOutlinedIcon from "@mui/icons-material/DragIndicatorOutlined";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import InteractionNodeInterval from "../../../models/interactions/InteractionNodeInterval";
import ConditionBar from "../../conditions/ConditionBar";
import InteractionConditionsButton from "../ui/InteractionConditionsButton";
import InteractionEditButton from "../ui/InteractionEditButton";
import Stack from "@mui/material/Stack";
import InteractionIntervalsButton from "../ui/InteractionIntervalsButton";
import IntervalModel from "../../../models/communications/Interval";
import {
  useRowSelector,
  useStoreDispatch,
  useStoreSelector,
} from "../../../store/Hooks";
import Interval from "../../communications/Interval";
import InteractionParentInterval from "../../../models/interactions/InteractionParentInterval";
import InteractionPopoutButton from "../ui/InteractionPopoutButton";
import InteractionTypeIndicator from "../ui/interactionpropdisplay/typeindicators/InteractionTypeIndicator";
import CacheFile from "../ui/interactionpropdisplay/cachefile/Cachefile";
import EventIcon from "./pages/ui/icons/EventIcon";
import { useParams } from "react-router-dom";
import { parseInteractionRouteParams } from "../../../models/interactions/InteractionRouteParam";
import SchedulingMatrixRow from "../../../models/communications/SchedulingMatrixRow";
import { getRandomInt } from "../../../helpers/DataUpdateHelper";
import MatrixInteractionRow from "../../communications/SchedulingMatrix/MatrixInteractionRow";
import InteractionDeleteButton from "../ui/InteractionDeleteButton";
import { interactionTreeSliceActions } from "../../../store/slices/InteractionTreeSlice";

const EventAccordionSummary = styled(AccordionSummary)(({ theme }) => {
  return {
    ".MuiAccordionSummary-content": {
      display: "block",
    },
  };
});

export type EventProps = {
  interaction: Interaction;
  interactionNode?: InteractionNode | null;
  interactionNodeIntervals: Array<InteractionNodeInterval>;
  isInitial: boolean;
  isReferenceSet: boolean;
  handleDeleteInteraction: any;
  dragPreview?: boolean | null | undefined;
  matrixRow?: SchedulingMatrixRow | null | undefined;
  ref?: any;
};

const Event = (props: EventProps) => {
  const params = useParams();
  const backgroundColor: string =
    props.dragPreview === true ? "lightyellow" : "";

  let thisParentIDFromParams: number | null;
  let thisNodeIDFromParams: number | null;
  let paramsArray: Array<any> = [];
  if (params.interactionPath) {
    // console.log(params.interactionPath)
    paramsArray = parseInteractionRouteParams(params.interactionPath);
    // take the last interaction in the route is the one we need to show
    thisParentIDFromParams = paramsArray[paramsArray.length - 1].parent;
    thisNodeIDFromParams = paramsArray[paramsArray.length - 1].nodeid;
  }

  const dispatch = useStoreDispatch();
  const interaction = useRowSelector(Interaction, props.interaction.id);
  // const interactionNode = useRowSelector(InteractionNode, props.interactionNode?.id || 0);

  const intervals = useStoreSelector(
    (state) => state.interactionTree.Intervals,
  );
  const previewMode = useStoreSelector((state) => state.nav.previewMode);

  const rootInteractionID = useStoreSelector(
    (state) => state.interactionTree.RootInteractionID,
  );

  const interactionNodeIntervals = useStoreSelector(
    (state) => state.interactionTree.InteractionNodeIntervals,
  );

  const interactionParentIntervals = useStoreSelector(
    (state) => state.interactionTree.InteractionParentIntervals,
  );

  const navHistory = useStoreSelector((state) => state.nav.navHistory);

  // these are intervals that associated with this interaction/node,
  // but are also not in an intervalSet
  const intervalsNoIntervalSet: Array<IntervalModel> = [];

  props.interactionNodeIntervals.forEach((interactionNodeInterval) => {
    const intervalIndex = intervals.findIndex(
      (interval) => interactionNodeInterval.interval_id === interval.id,
    );
    if (intervalIndex > -1) {
      const thisInterval = intervals[intervalIndex];
      if (!thisInterval.interval_set_id) {
        intervalsNoIntervalSet.push(thisInterval);
      }
    }
  });
  let intervalsForThisInteraction: Array<any> = [];

  // event is NOT the root interaction showm (its not "popped out")
  // and is shown as a child node with a pathway as the root "popped out" interaction
  if (props.interaction.id !== rootInteractionID) {
    intervalsForThisInteraction = intervalsNoIntervalSet.map((interval) => {
      // sometimes we were already able to grab the parent from the URL
      // if so, just use that as its more explicit
      let parent = thisParentIDFromParams;
      if (props.interactionNode && props.interactionNode.parent_interaction_id && !parent) {
        parent = props.interactionNode.parent_interaction_id;
      }

      // for each of these intervals, is there a corresponding parent interval for that intervalID?
      // there should be only one (confirm with Mark? because the db doesnt constrain this as of 4/13/2023)
      const interactionParentInterval: InteractionParentInterval | null =
        intervalsNoIntervalSet.map((associatedInterval: any) => {
          const intervalMatch: InteractionParentInterval | null =
            interactionParentIntervals.filter((item) => {
              if (props.interactionNode && props.interactionNode.parent_interaction_id) {
                return (
                  associatedInterval.id === item.interval_id &&
                  props.interactionNode.parent_interaction_id === item.interaction_parent_id
                );
              } else {
                return false;
              }
            })[0];
          return intervalMatch;
        })[0];
      return (
        <Interval
          key={interval.id}
          interactionParentInterval={interactionParentInterval}
          parentID={parent}
          interval={interval}
        />
      );
    });
    // event IS the root interaction shown,
    // and is essentially "popped out" to only see this one
  } else {
    let thisNode: InteractionNode | null = null;
    if (navHistory.length > 1) {
      const thisNavHistory = navHistory[navHistory.length - 2];
      // console.log(thisNavHistory);
      let directAncestorNodeIndex: any;
      if (
        thisNavHistory.state.interaction_nodes &&
        thisNavHistory.state.interaction_nodes.length > 0
      ) {
        // a "popped out" event CAN be properly rendered without nodeID info passed in,
        // but without it, the system might get confused if there are multiple nodes
        // that share a single child interaction ref
        // using passed in nodeID helps to confirm that we wont grab the wrong node
        if (params.interactionPath) {
          // let paramsArray = parseInteractionRouteParams(params.interactionPath);

          // take the last interaction in the route is the one we need to show
          thisParentIDFromParams = paramsArray[paramsArray.length - 1].parent;
          thisNodeIDFromParams = paramsArray[paramsArray.length - 1].nodeid;

          if (thisNodeIDFromParams) {
            directAncestorNodeIndex =
              thisNavHistory.state.interaction_nodes.findIndex(
                (interaction_node: InteractionNode) => {
                  return (
                    interaction_node.child_interaction_id === props.interaction.id &&
                    thisNodeIDFromParams == interaction_node.id
                  );
                },
              );
          } else {
            directAncestorNodeIndex =
              thisNavHistory.state.interaction_nodes.findIndex(
                (interaction_node: InteractionNode) => {
                  return interaction_node.child_interaction_id === props.interaction.id;
                },
              );
          }
        }
        thisNode =
          thisNavHistory.state.interaction_nodes[directAncestorNodeIndex];
      }
    }

    if (thisNode) {
      for (let i = 0; i < intervals.length; i++) {
        // intervals.forEach((interval) => {
        let parent: any = null;
        if (thisNode && thisNode.parent_interaction_id) {
          parent = thisNode.parent_interaction_id;
        }
        for (let j = 0; j < interactionNodeIntervals.length; j++) {
          if (
            interactionNodeIntervals[j].interval_id === intervals[i].id &&
            interactionNodeIntervals[j].interaction_node_id === thisNode?.id
          ) {
            intervalsForThisInteraction.push(
              <Interval
                interactionParentInterval={null}
                parentID={parent}
                interval={intervals[i]}
              />,
            );
            // break loop1;
          }
        }
      }
    }
  }

  // if interaction is "popped out",
  // we treat it differently then when its displayed in the tree
  if (props.interaction.id === rootInteractionID) {
    return (
      <div style={{ margin: "5px", padding: "10px 60px", cursor: "grab" }}>
        {/* {!draggingLocal && */}

        {props.interactionNode &&
          props.interactionNode.condition_id !== null && (
            <div>
              <ConditionBar
                sx={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  width: "100%",
                }}
                table={InteractionNode}
                tableID={props.interactionNode.id}
              />
            </div>
          )}

        <div style={{ margin: "10px", padding: "10px 60px" }}>
          <span style={{ float: "right" }}>
            <InteractionTypeIndicator
              type={props.interaction.properties.type}
              variant="outlined"
            />
            <CacheFile {...props.interaction.properties} />
          </span>
          <h2>
            <span style={{ float: "left" }}>{props.interaction.title}</span>
            <Stack
              // justifyContent="flex-start"
              direction="row"
              spacing={0}
            >
              <Stack
                // justifyContent="flex-start"
                direction="row"
                spacing={1}
              >
                {!previewMode && (
                  <InteractionIntervalsButton
                    interaction={props.interaction}
                    interactionNode={props.interactionNode}
                    insertFunction={IntervalModel.addNewInterval}
                  />
                )}
                {props.interactionNode !== null &&
                  typeof props.interactionNode !== "undefined" && (
                    <InteractionConditionsButton
                      interaction={props.interaction}
                      condition_id={props.interactionNode.condition_id}
                      interaction_node={props.interactionNode}
                    />
                  )}
                {!previewMode && (
                  <div style={{ marginTop: "0px" }}>
                    <InteractionEditButton interaction={props.interaction} />
                  </div>
                )}
                {!previewMode && (
                  <div style={{ marginTop: "0px" }}>
                    <InteractionDeleteButton
                      interaction={props.interaction}
                      handler={props.handleDeleteInteraction}
                    />
                  </div>
                )}
              </Stack>
              <span> &nbsp; &nbsp;</span>
            </Stack>
          </h2>
          <Divider />
          <br />
          <br />
          {intervalsForThisInteraction &&
            intervalsForThisInteraction.length > 0 && (
              <div
                style={{
                  paddingLeft: "40px",
                  paddingRight: "20px",
                  paddingBottom: "20px",
                  border: "1pt solid #e3e3e3",
                }}
              >
                <h3>Assigned Intervals:</h3>

                <Box sx={{ flexGrow: 1 }}>
                  <Stack
                    // style={{ position: "absolute" }}
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                  >
                    {intervalsForThisInteraction}
                  </Stack>
                </Box>
              </div>
            )}
        </div>
      </div>
    );

    // handle interaction as its displayed in a tree in context
  } else if (props.matrixRow) {
    return (
      <MatrixInteractionRow
        key={props.matrixRow?.interaction_node.id + getRandomInt(0, 10000000)}
        {...props.matrixRow}
        editMode={true}
        handleDeleteInteraction={props.handleDeleteInteraction}
        // intervalSetName={props.intervalSetName}
        // insertionPoint={allInteractionRow.insertionPoint}
        // intervalSetID = {props.intervalSetID}
      />
    );
  } else {
    return (
      <div
        style={{
          margin: "5px",
          padding: "10px 60px",
          cursor: "grab",
          backgroundColor: backgroundColor,
        }}
      >
        {/* {!draggingLocal && */}
        <Accordion expanded={false}>
          <EventAccordionSummary
            aria-controls="panel1bh-content"
            id="panel1bh-header"
            style={{
              border: "1px solid lightskyblue",
              borderRadius: "5px",
              backgroundColor: "#f0f0f0",
              width: "100%",
              cursor: "grab",
            }}
          >
            {props.interactionNode &&
              props.interactionNode.condition_id !== null && (
                <ConditionBar
                  sx={{
                    position: "relative",
                    left: -16,
                    top: -12,
                    width: "calc(100% + 32px)",
                    borderTopLeftRadius: 4,
                    borderTopRightRadius: 4,
                  }}
                  table={InteractionNode}
                  tableID={props.interactionNode.id}
                />
              )}

            <Box sx={{ flexGrow: 1 }}>
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <h4>
                    {!previewMode && (
                      <DragIndicatorOutlinedIcon
                        style={{
                          fontSize: "2.5em !important",
                          margin: "0 25px 0 10px",
                        }}
                      />
                    )}
                    {previewMode && (
                      <span>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                      </span>
                    )}
                    {props.interaction.title}
                  </h4>
                </Grid>
                <Grid item xs={4}>
                  <Stack
                    style={{ position: "absolute", marginTop: "20px" }}
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                  >
                    {intervalsForThisInteraction}
                  </Stack>
                </Grid>
                <Grid item xs={4}>
                  <Stack
                    justifyContent="flex-end"
                    style={{
                      position: "absolute",
                      marginTop: "20px",
                      textAlign: "right",
                    }}
                    direction="row"
                    spacing={0}
                    flexWrap="wrap"
                  >
                    {props.interactionNode && (
                      <InteractionPopoutButton
                        interaction={props.interaction}
                        interactionNode={props.interactionNode}
                        setID={props.interactionNode.set_interaction_id}
                        parentID={props.interactionNode.parent_interaction_id}
                        isInitial={props.isInitial}
                        isReferenceSet={props.isReferenceSet}
                      />
                    )}
                    {!previewMode && (
                      <InteractionIntervalsButton
                        interaction={props.interaction}
                        interactionNode={props.interactionNode}
                        insertFunction={IntervalModel.addNewInterval}
                      />
                    )}
                    {props.interactionNode !== null &&
                      typeof props.interactionNode !== "undefined" &&
                      !previewMode && (
                        <InteractionConditionsButton
                          interaction={props.interaction}
                          condition_id={props.interactionNode.condition_id}
                          interaction_node={props.interactionNode}
                        />
                      )}
                    {!previewMode && (
                      <InteractionEditButton interaction={props.interaction} />
                    )}
                    {!previewMode && (
                      <InteractionDeleteButton
                        handler={props.handleDeleteInteraction}
                      />
                    )}
                  </Stack>
                  <EventIcon />
                </Grid>
              </Grid>
            </Box>
          </EventAccordionSummary>
        </Accordion>
      </div>
    );
  }
};

export default Event;
